import { parseJwt } from '../utils/jwtDecoder';
import { getItem } from '../utils/authStorage';
import { fetchWithAuthRetry, getAuthHeaders, ensureValidToken } from '../utils/helpers';

async function tenantHeaders() {
  await ensureValidToken();
  const token = getItem('accessToken');
  const payload = token ? parseJwt(token) : null;
  const tenantId = payload?.tenant_id || '';
  return {
    ...getAuthHeaders(),
    ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
  };
}

/**
 * List construction site surveys for a project (numeric id or code).
 * @param {string|number} projectId
 * @returns {Promise<Array>}
 */
export async function fetchSurveys(projectId) {
  if (!projectId) return [];
  const endpoint = `/api/construction/projects/${encodeURIComponent(projectId)}/surveys`;
  try {
    const headers = await tenantHeaders();
    const response = await fetchWithAuthRetry(endpoint, { headers });
    if (!response.ok) {
      console.warn(`GET ${endpoint} → HTTP ${response.status}`);
      return [];
    }
    const json = await response.json();
    return Array.isArray(json) ? json.map(normalizeSurvey) : [];
  } catch (err) {
    console.error('fetchSurveys error:', err);
    return [];
  }
}

/**
 * List site images for a survey. type: '2D' | '3D' | 'WALKTHROUGH' | undefined
 */
export async function fetchSurveyImages(projectId, surveyId, type) {
  if (!projectId || !surveyId) return [];
  const params = type ? `?type=${encodeURIComponent(type)}` : '';
  const endpoint = `/api/construction/projects/${encodeURIComponent(projectId)}/surveys/${surveyId}/images${params}`;
  try {
    const headers = await tenantHeaders();
    const response = await fetchWithAuthRetry(endpoint, { headers });
    if (!response.ok) {
      console.warn(`GET ${endpoint} → HTTP ${response.status}`);
      return [];
    }
    const json = await response.json();
    return Array.isArray(json) ? json.map(normalizeImage) : [];
  } catch (err) {
    console.error('fetchSurveyImages error:', err);
    return [];
  }
}

function normalizeSurvey(s) {
  return {
    id: s.id,
    projectId: s.projectId,
    surveyDate: s.surveyDate,
    dateLabel: s.dateLabel || s.surveyDate || '',
    latest: Boolean(s.latest),
    drone: s.drone || '—',
    capturedImages: s.capturedImages != null ? String(s.capturedImages) : '—',
    groundResolution: s.groundResolution || '—',
    areaCovered: s.areaCovered || '—',
    weather: s.weather || '—',
    flightDuration: s.flightDuration || '—',
    notes: s.notes || '',
  };
}

function normalizeImage(img) {
  return {
    id: img.id,
    surveyId: img.surveyId,
    projectId: img.projectId,
    type: img.type,
    name: img.name,
    filePath: img.filePath,
    previewPath: img.previewPath,
    tilePath: img.tilePath,
    sortOrder: img.sortOrder ?? 0,
    url: img.url || null,
    previewUrl: img.previewUrl || img.url || null,
    tileUrl: img.tileUrl || null,
  };
}
